<template>
  <gl-modal
    closable
    closable-top
    info-only
    :title="modalTitle"
    :value="value"
    width="480"
    @close="closeModal"
  >
    <template #before-header-action>
      <gl-icon
        class="pointer"
        :height="24"
        name="report-back-arrow"
        :width="24"
        @click="goBack"
      />
    </template>
    <template #content>
      <div class="confirm-modal__body">
        <div
          v-if="toTrialPan"
          class="flex align-center justify-center"
        >
          <GlSelectButton
            gray-padding="wide"
            grey
            :options="computedPlanTypes"
            :value="activeType"
            @input="typeChange"
          />
        </div>

        <div class="confirm-modal__card">
          <div class="confirm-modal__price-section">
            <div class="confirm-modal__price-row">
              <span
                v-if="toTrialPan"
                class="confirm-modal__price--trial"
              >
                7 days for free
              </span>
              <span
                v-else
                class="confirm-modal__price"
              >
                {{ displayPrice }}
              </span>

              <span
                v-if="toTrialPan"
                class="confirm-modal__asterisk"
              >
                *
              </span>
              <span
                v-else-if="actionStatus !== 'refresh-quota'"
                class="confirm-modal__billing"
              >
                / {{ cycleShortLabel }}
              </span>
            </div>

            <div class="confirm-modal__subtitle mb-1">
              {{ subtitleText }}
            </div>
            <div
              class="confirm-modal__subtitle"
              v-html="subtitleExplain"
            />
          </div>

          <div class="confirm-modal__benefits-section">
            <PlanBenefitsList :benefits="plan.benefits" />
          </div>
        </div>

        <div class="confirm-modal__actions">
          <GlButton
            dark
            full
            :loading="loading"
            size="large"
            :title="toTrialPan ? 'Start 7-day Free Trial' : 'Confirm'"
            @click="$emit('confirm', activeType ? activeType.value : null)"
          />
        </div>
      </div>
    </template>
  </gl-modal>
</template>

<script>
// Vuex
import { mapGetters } from 'vuex';

// Components
import GlIcon from "@/components/gl-icon.vue";
import GlModal from '@/components/gl-modal.vue';
import GlButton from "@/components/gl-button.vue";
import GlSelectButton from "@/components/gl-select-button2.vue";
import PlanBenefitsList from '@/pages/profile/components/PlanBenefitsList.vue';

// Utils
import { billingCycle, getAnnualSaveDiffFromPlan, getPlanOptionsWithProfit } from "@/utils/plans";

export default {
  name: 'ConfirmPlanActionModal',
  components: {
    GlIcon,
    GlSelectButton,
    GlButton,
    GlModal,
    PlanBenefitsList
  },
  props: {
    value: { type: Boolean, default: false },
    plan: { type: Object, required: true },
    currentPlan: { type: Object, required: true },
    actionStatus: { type: String, required: true },
    activeTypeValue: { type: String, default: null },
    loading: { type: Boolean, default: false }
  },
  data: () => ({
    activeType: null,
  }),
  computed: {
    ...mapGetters('payment', ['getPlans']),
    rawPlan() {
      if (!this.getPlans || !this.plan?.id) return null;
      return this.getPlans.find(p => p.tier === this.plan.id);
    },

    isTrial() {
      const cp = this.currentPlan;
      return cp?.status === 'trialing' || (cp?.trialEnd && !cp?.trialUsed);
    },

    toTrialPan() {
      const query = this.$route.query.isTrialRequest;

      if (query === 'false') return false;
      if (query === 'true') return true;

      return this.isTrial && this.actionStatus === 'upgrade';
    },

    targetCycle() {
      if (this.activeType?.value) {
        return this.activeType.value === 'annual' ? 'annual' : 'monthly';
      }
      return this.plan.billing?.includes('month') ? 'monthly' : 'annual';
    },

    cycleText() {
      return this.targetCycle === 'annual' ? 'annually' : 'monthly';
    },

    cycleShortLabel() {
      return this.targetCycle === 'annual' ? 'year' : 'month';
    },

    displayPrice() {
      if (this.actionStatus === 'refresh-quota') return this.plan.totalPrice;

      return this.targetCycle === 'annual'
          ? this.plan.priceAnnually
          : this.plan.priceMonthly;
    },

    formattedDate() {
      const dateSource = this.toTrialPan ? this.currentPlan?.trialEnd : this.currentPlan?.currentPeriodEnd;
      if (!dateSource) return '';

      return new Date(dateSource).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    },

    modalTitle() {
      if (this.actionStatus === 'refresh-quota') return this.plan.planName;
      if (this.actionStatus === 'switch-cycle') return `Switch to ${this.cycleText} billing?`;
      if (this.toTrialPan) return `Confirm ${this.plan.planName} Plan`;
      if (this.actionStatus === 'upgrade') return `Upgrade to ${this.plan.planName} Plan`;
      if (this.actionStatus === 'downgrade') return `Downgrade to ${this.plan.planName} Plan`;
      if (this.actionStatus === 'renew') return `Renew Subscription`;

      return 'Confirm Action';
    },

    subtitleExplain() {
      if (this.actionStatus === 'refresh-quota') return this.plan.planDescription;

      const { isTrial, toTrialPan, targetCycle, actionStatus } = this;

      if (actionStatus === 'upgrade') {
        if (toTrialPan) return '';
        if (isTrial) return '';
        if (targetCycle === 'annual') {
          return `A new billing cycle will start today. You will be charged the full amount of <strong>${this.displayPrice}</strong> upon confirmation.`;
        }
      }

      return '';
    },

    subtitleText() {
      if (this.actionStatus === 'refresh-quota') {
        return 'One-time payment · Upgrades immediately';
      }

      const {
        plan,
        isTrial,
        rawPlan,
        cycleText,
        toTrialPan,
        currentPlan,
        targetCycle,
        actionStatus,
        formattedDate
      } = this;

      if (actionStatus === 'upgrade') {
        if (toTrialPan) {
          const disclaimer = "Your card will not be charged today.";
          if (targetCycle === 'annual' && rawPlan?.prices) {
            const currencySymbol = plan.priceMonthly.replace(/[\d.\s,-]/g, '') || '€';
            return `${disclaimer} Then ${currencySymbol}${rawPlan.prices.annualPerMonth} / month. Billed ${plan.priceAnnually} annually.`;
          }
          return `${disclaimer} You will be billed ${plan.priceMonthly} / month after 7 days.`;
        }
        return isTrial
            ? `Billed ${cycleText} · Upgrades immediately · Remaining trial days will be honored`
            : `Billed ${cycleText} · Upgrades immediately`;
      }

      if (actionStatus === 'downgrade') {
        return isTrial
            ? `Billed ${cycleText} · Downgrades immediately · Your paid subscription starts today, and the trial ends.`
            : `Billed ${cycleText} · Will downgrade on ${formattedDate}`;
      }

      if (actionStatus === 'switch-cycle') {
        const isMonthlyToAnnual =
            (currentPlan?.billingCycle === 'month' || currentPlan?.billingCycle === 'monthly') &&
            targetCycle === 'annual';

        if (isTrial) {
          if (isMonthlyToAnnual) {
            return `Billed ${cycleText} · Will switch on ${formattedDate}`;
          } else {
            return `Billed ${cycleText} · Downgrades immediately · Your paid subscription starts today, and the trial ends.`;
          }
        }

        if (isMonthlyToAnnual) {
          return `Billed ${cycleText} · Upgrades immediately`;
        }

        return `Billed ${cycleText} · Will switch on ${formattedDate}`;
      }

      return '';
    },

    computedPlanTypes() {
      const diff = getAnnualSaveDiffFromPlan(this.rawPlan);
      const currencySymbol = this.plan?.priceMonthly?.replace(/[\d.\s,-]/g, '') || '€';
      return getPlanOptionsWithProfit(billingCycle, diff, currencySymbol);
    },
    localActiveCycle() {
      return this.activeTypeValue || this.currentPlan?.billingCycle
    },
  },
  watch: {
    currentPlan: {
      immediate: true,
      handler() {
        this.initActiveType();
      }
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    closeModal() {
      if (this.loading) return;
      this.$emit('input', false);
      this.$emit('close');
    },
    initActiveType() {
      this.activeType = this.computedPlanTypes.find(t => t.value === this.localActiveCycle) || this.computedPlanTypes[0];
    },
    typeChange(type) {
      this.activeType = type;
    },
  }
};
</script>

<style scoped>
.confirm-modal__body {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.confirm-modal__card {
  border: 1px solid var(--Basic-200, rgba(9, 21, 64, 0.08));
  border-radius: 20px;
  background: var(--white);
  overflow: hidden;
}

.confirm-modal__price-section {
  padding: 24px;
  border-bottom: 1px solid var(--Basic-200, rgba(9, 21, 64, 0.08));
}

.confirm-modal__price-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 8px;
}

.confirm-modal__price {
  color: var(--Basic-900, #091540);
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
}

.confirm-modal__price--trial {
  color: var(--Basic-900, #091540);
  font-size: 24px;
  font-weight: 700;
  line-height: var(--Line-Height-3XL, 32px);
}

.confirm-modal__asterisk {
  color: var(--Error-Main, #FF4754);
  font-size: 24px;
  font-weight: 700;
  line-height: var(--Line-Height-3XL, 32px);
}

.confirm-modal__billing,
.confirm-modal__subtitle {
  font-size: var(--Size-S, 12px);
  font-weight: 500;
  line-height: var(--Line-Height-S, 16px);
}

.confirm-modal__billing {
  color: var(--Basic-800, rgba(9, 21, 64, 0.68));
}

.confirm-modal__subtitle {
  color: var(--Basic-900, #091540);
}

.confirm-modal__benefits-section {
  padding: 20px 24px 24px 24px;
}

.confirm-modal__actions {
  width: 100%;
}
</style>
